import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { UserPlus, Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [autoRegistration, setAutoRegistration] = useState(false);
  const [courseInfo, setCourseInfo] = useState<any>(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const courseId = searchParams.get('courseId');

  useEffect(() => {
    // Si viene con courseId, es auto-registro desde landing page
    if (courseId) {
      setAutoRegistration(true);
      loadCourseInfo();
    }
  }, [courseId]);

  const loadCourseInfo = async () => {
    try {
      if (!courseId) return;
      
      const response = await apiClient.get(`/courses/${courseId}`);
      setCourseInfo(response.data.course);
    } catch (error) {
      console.error('Error loading course info:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Para auto-registro (desde landing page)
    if (autoRegistration) {
      // Solo validar campos requeridos (no contraseña ya que se genera automáticamente)
      if (!formData.firstName || !formData.lastName || !formData.email) {
        setError('Todos los campos son requeridos');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.post('/auto-registration/process', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          courseId: courseId
        });

        if (response.data.success) {
          setSuccess(true);
          setError('');
          
          // Mostrar mensaje diferente basado en si es gratuito o pago
          const message = response.data.isFree 
            ? 'Cuenta creada y activada. Revisa tu correo con las credenciales.'
            : 'Inscripción procesada. Revisa tu correo y completa el pago para activar tu cuenta.';
          
          setError(message); // Usar setError para mostrar el mensaje (aunque sea exitoso)
          
          // Para cursos gratuitos, redirigir al login después de unos segundos
          if (response.data.isFree) {
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          }
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Error en el registro automático');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Registro normal (validaciones completas)
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      });
      
      setSuccess(true);
      
      // Redirigir al dashboard después de 2 segundos
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
      <div className="w-full max-w-lg">
        {/* Logo y branding */}
        <div className="text-center mb-8">
          <div className="relative mb-6">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/25">
              <UserPlus size={40} className="text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-400 rounded-full animate-pulse"></div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent mb-2">
            Únete a Agentesia Academy
          </h1>
          <p className="text-slate-300 text-lg font-medium">
            Crea tu cuenta gratuita
          </p>
          <p className="text-slate-400 text-sm mt-1">
            Accede a cursos premium de Inteligencia Artificial
          </p>
        </div>

        {/* Course info for auto-registration */}
        {autoRegistration && courseInfo && (
          <div className="bg-gradient-to-r from-blue-600/20 via-purple-600/10 to-cyan-600/20 backdrop-blur-sm rounded-3xl p-6 border border-blue-400/30 mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <UserPlus className="text-blue-400" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{courseInfo.title}</h3>
                <p className="text-blue-300">
                  {courseInfo.price > 0 
                    ? `Curso Premium - $${courseInfo.price.toLocaleString()}` 
                    : 'Curso Gratuito'}
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-white/10 rounded-xl">
              <p className="text-slate-300 text-sm">
                {courseInfo.price > 0 
                  ? '🎓 Inscríbete ahora y completa el pago para acceder a todo el contenido premium.'
                  : '🎓 ¡Genial! Este curso es gratuito. Tu cuenta se activará automáticamente tras el registro.'}
              </p>
            </div>
          </div>
        )}

        {/* Form container */}
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="space-y-6">

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 backdrop-blur-sm">
                <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={18} className="text-red-400" />
                </div>
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3 backdrop-blur-sm">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={18} className="text-emerald-400" />
                </div>
                <div className="text-emerald-300 text-sm">
                  <p className="font-semibold">¡Registro exitoso!</p>
                  <p>Hemos enviado un correo de confirmación a tu email. Serás redirigido al dashboard en unos segundos...</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-slate-200 mb-3">
                    Nombre
                  </label>
                  <div className="relative">
                    <User size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" />
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                      placeholder="Juan"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-slate-200 mb-3">
                    Apellido
                  </label>
                  <div className="relative">
                    <User size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" />
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                      placeholder="Pérez"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-200 mb-3">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
              </div>

              {/* Campos de contraseña solo para registro normal */}
              {!autoRegistration && (
                <>
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-slate-200 mb-3">
                      Contraseña
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" />
                      <input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Mínimo 6 caracteres</p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-200 mb-3">
                      Confirmar Contraseña
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" />
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Información para auto-registro */}
              {autoRegistration && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <p className="text-yellow-300 text-sm text-center">
                    🔐 <strong>Contraseña automática:</strong> Generaremos una contraseña segura y te la enviaremos por correo junto con tus credenciales de acceso.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25 text-lg flex items-center justify-center gap-3"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <UserPlus size={20} />
                    {autoRegistration ? 'Inscribirse al Curso' : 'Crear Cuenta Gratuita'}
                  </>
                )}
              </button>
            </form>
          </div>

        </div>

        {/* Links adicionales */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-slate-300">
            ¿Ya tienes cuenta?{' '}
            <Link
              to="/login"
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200 hover:underline"
            >
              Iniciar sesión aquí
            </Link>
          </p>
          
          <div className="flex items-center gap-4 justify-center">
            <div className="h-px bg-slate-600 flex-1"></div>
            <span className="text-slate-400 text-sm px-2">o</span>
            <div className="h-px bg-slate-600 flex-1"></div>
          </div>
          
          <a
            href="https://escuela.agentesia.cloud"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm transition-colors duration-200"
          >
            ← Volver al sitio principal
          </a>
        </div>

        {/* Términos y condiciones */}
        <div className="mt-8 p-6 bg-slate-800/30 rounded-2xl border border-slate-700/50 text-center">
          <p className="text-sm text-slate-400 leading-relaxed">
            Al crear una cuenta, aceptas nuestros{' '}
            <a
              href="https://escuela.agentesia.cloud/terminos.html"
              className="text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              términos de servicio
            </a>{' '}
            y{' '}
            <a
              href="https://escuela.agentesia.cloud/privacidad.html"
              className="text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              política de privacidad
            </a>
          </p>
        </div>

        {/* Features preview */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
            <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <UserPlus size={16} className="text-blue-400" />
            </div>
            <p className="text-xs text-slate-300 font-medium">Cuenta Gratis</p>
          </div>
          <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <Lock size={16} className="text-emerald-400" />
            </div>
            <p className="text-xs text-slate-300 font-medium">100% Seguro</p>
          </div>
          <div className="text-center p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
            <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <Mail size={16} className="text-purple-400" />
            </div>
            <p className="text-xs text-slate-300 font-medium">Sin Spam</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;